export class CartSizeConflictModal {
  static async resolve({ productForm, formData }) {
    const variantId = String(formData.get('id') || '').trim();
    const productId = String(productForm?.dataset.productId || '').trim();

    if (!variantId || !productId) {
      return { action: 'continue' };
    }

    const selectedVariant = this.getSelectedVariant(productForm, variantId);

    if (!selectedVariant) {
      return { action: 'continue' };
    }

    const selectedColor = this.getVariantOption(selectedVariant, 0);
    const selectedSize = this.getVariantOption(selectedVariant, 1);

    if (!selectedColor || !selectedSize) {
      return { action: 'continue' };
    }

    const cart = await this.fetchCart();

    const conflicts = (cart.items || []).filter((item) => {
      if (String(item.product_id) !== productId) return false;
      if (String(item.variant_id) === variantId) return false;

      const itemColor = this.getCartItemOption(item, 'color', 0);
      const itemSize = this.getCartItemOption(item, 'size', 1);

      return (
        this.normalize(itemColor) === this.normalize(selectedColor) &&
        this.normalize(itemSize) !== this.normalize(selectedSize)
      );
    });

    if (!conflicts.length) {
      return { action: 'continue' };
    }

    const choice = await this.openModal({
      existingItem: conflicts[0],
      selectedVariant,
      selectedColor,
      selectedSize,
      advisorId: this.getSizeAdvisorId(productForm),
    });

    if (!choice || choice.action === 'close') {
      return { action: 'abort' };
    }

    const keepExisting = Boolean(choice.keepExisting);
    const keepNew = Boolean(choice.keepNew);

    if (keepExisting && keepNew) {
      return {
        action: 'continue',
        shouldRefreshAddIntent: true,
      };
    }

    if (keepExisting && !keepNew) {
      return { action: 'abort' };
    }

    if (!keepExisting && keepNew) {
      return {
        action: 'remove-existing-then-add',
        itemsToRemove: conflicts,
        shouldRefreshAddIntent: true,
      };
    }

    return { action: 'abort' };
  }

  static async removeItems(items) {
    for (const item of items) {
      if (!item.key) continue;

      await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0,
        }),
      });
    }
  }

  static markConfirmedAddIntent({ variantId = '', imageUrl = '', imageAlt = '' } = {}) {
    document.dispatchEvent(
      new CustomEvent('cart-size-conflict:add-intent', {
        detail: {
          variantId,
          imageUrl,
          imageAlt,
        },
      })
    );
  }

  static getSelectedVariant(productForm, variantId) {
    const productId = String(productForm.dataset.productId || '');
    const scope = productForm.closest('quick-add-dialog, dialog, product-card, .shopify-section') || document;

    const escapedProductId = window.CSS?.escape ? window.CSS.escape(productId) : productId;

    const variantPicker =
      scope.querySelector(`variant-picker[data-product-id="${escapedProductId}"]`) ||
      document.querySelector(`variant-picker[data-product-id="${escapedProductId}"]`);

    const script = variantPicker?.querySelector('script[type="application/json"]');

    if (!script?.textContent) return null;

    try {
      const variant = JSON.parse(script.textContent);

      if (String(variant.id) !== variantId) {
        return null;
      }

      return variant;
    } catch (error) {
      console.error('Variant JSON konnte nicht gelesen werden:', error);
      return null;
    }
  }

  static getVariantOption(variant, index) {
    return String(variant?.options?.[index] || variant?.[`option${index + 1}`] || '').trim();
  }

  static getCartItemOption(item, type, fallbackIndex) {
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];

    const option = options.find((option) => {
      const name = this.normalizeOptionName(option.name);

      if (type === 'color') {
        return name === 'color' || name === 'colour' || name === 'farbe';
      }

      if (type === 'size') {
        return name === 'size' || name === 'groesse' || name === 'grosse';
      }

      return false;
    });

    return String(option?.value || item.options?.[fallbackIndex] || item[`option${fallbackIndex + 1}`] || '').trim();
  }

  static getSizeAdvisorId(productForm) {
    const scope = productForm.closest('quick-add-dialog, dialog, product-card, .shopify-section') || document;
    const trigger = scope.querySelector('[data-custom-size-advisor-open]');

    return trigger?.getAttribute('data-custom-size-advisor-open') || '';
  }

  static normalizeOptionName(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replaceAll('ß', 'ss')
      .replaceAll('ö', 'oe')
      .replaceAll('ä', 'ae')
      .replaceAll('ü', 'ue');
  }

  static normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[–—]/g, '-');
  }

  static async fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Warenkorb konnte nicht geladen werden.');
    }

    return response.json();
  }

  static getCheckmarkIcon() {
    return document.getElementById('cart-size-conflict-checkmark-icon')?.innerHTML.trim() || '';
  }

  static openModal({ existingItem, selectedVariant, selectedColor, selectedSize, advisorId }) {
    const existingColor = this.getCartItemOption(existingItem, 'color', 0);
    const existingSize = this.getCartItemOption(existingItem, 'size', 1);

    document.querySelector('.cart-size-conflict-modal')?.remove();

    const template = document.getElementById('custom-cart-size-conflict-modal-template');

    if (!(template instanceof HTMLTemplateElement)) {
      throw new Error('Cart size conflict modal template is missing.');
    }

    const dialog = template.content.firstElementChild.cloneNode(true);

    if (!(dialog instanceof HTMLDialogElement)) {
      throw new Error('Cart size conflict modal template must contain a dialog element.');
    }

    document.body.appendChild(dialog);

    const existingImage = existingItem.image || '';
    const newImage =
      selectedVariant?.featured_image?.src ||
      selectedVariant?.featured_media?.preview_image?.src ||
      '';

    dialog.querySelector('[data-existing-image]').innerHTML = existingImage
      ? `<img src="${this.escapeHtml(existingImage)}" alt="">`
      : '';

    dialog.querySelector('[data-new-image]').innerHTML = newImage
      ? `<img src="${this.escapeHtml(newImage)}" alt="">`
      : '';

    dialog.querySelector('[data-existing-title]').textContent = existingItem.product_title || existingItem.title || '';
    dialog.querySelector('[data-new-title]').textContent = existingItem.product_title || selectedVariant.name || '';

    this.renderMeta(dialog.querySelector('[data-existing-meta]'), existingColor, existingSize);
    this.renderMeta(dialog.querySelector('[data-new-meta]'), selectedColor, selectedSize);

    const existingInput = dialog.querySelector('[data-conflict-choice="existing"]');
    const newInput = dialog.querySelector('[data-conflict-choice="new"]');
    const advisorButton = dialog.querySelector('[data-conflict-size-advisor]');
    const confirmButton = dialog.querySelector('[data-conflict-confirm]');

    existingInput.checked = false;
    newInput.checked = true;
    advisorButton.hidden = !advisorId;

    const updateConfirmState = () => {
      const hasSelection = existingInput.checked || newInput.checked;

      confirmButton.disabled = !hasSelection;
      confirmButton.setAttribute('aria-disabled', hasSelection ? 'false' : 'true');
      dialog.classList.toggle('cart-size-conflict-modal--no-selection', !hasSelection);
    };

    updateConfirmState();

    return new Promise((resolve) => {
      let isResolved = false;

      const close = (value) => {
        if (isResolved) return;
        isResolved = true;

        if (dialog.open) {
          dialog.close();
        }

        resolve(value);
      };

      existingInput.onchange = updateConfirmState;
      newInput.onchange = updateConfirmState;

      dialog.querySelector('[data-conflict-close]').onclick = () => close({ action: 'close' });

      dialog.querySelector('[data-conflict-confirm]').onclick = () => {
        const keepExisting = existingInput.checked;
        const keepNew = newInput.checked;

        if (!keepExisting && !keepNew) {
          error.hidden = false;
          return;
        }

        close({
          keepExisting,
          keepNew,
        });
      };

      advisorButton.onclick = () => {
        close({ action: 'close' });

        window.setTimeout(() => {
          const advisor = advisorId ? document.getElementById(advisorId) : null;

          if (advisor && typeof advisor.open === 'function') {
            advisor.open();
          }
        }, 120);
      };

      dialog.oncancel = (event) => {
        event.preventDefault();
        close({ action: 'close' });
      };

      dialog.onclick = (event) => {
        if (event.target === dialog) {
          close({ action: 'close' });
        }
      };

      dialog.showModal();
    });
  }

  static renderMeta(element, color, size) {
    if (!element) return;

    const dialog = element.closest('.cart-size-conflict-modal');
    const sizeLabel =
      dialog?.querySelector('[data-conflict-size-label]')?.textContent?.trim() || 'Größe';

    element.replaceChildren();

    if (color) {
      const colorSpan = document.createElement('span');
      colorSpan.className = 'cart-size-conflict-modal__meta-color';
      colorSpan.textContent = color;
      element.append(colorSpan);
    }

    if (size) {
      const sizeSpan = document.createElement('span');
      sizeSpan.className = 'cart-size-conflict-modal__meta-size';

      const label = document.createElement('span');
      label.textContent = `${sizeLabel} `;

      const strong = document.createElement('strong');
      strong.className = 'cart-size-conflict-modal__size-value';
      strong.textContent = size;

      sizeSpan.append(label, strong);
      element.append(sizeSpan);
    }
  }

  static escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }
}